import {
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  BookingPermission,
  CouponPermission,
  CustomFieldPermission,
  InvoicePermission,
  RoomPermission,
  UserPermission,
  VendorPermission,
} from '../../core/models/entities/permission';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { ModalService } from '../../core/services/modal.service';
import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { map } from 'rxjs/operators';
import { LogoutBaseComponent } from '../../shared/components/logout-base/logout-base.component';
import { AppLocale, LocaleService } from '../../core/services/locale.service';
import { TranslocoService } from '@jsverse/transloco';
import { ChangelogService } from 'angular/app/changelog/services/changelog.service';
import { ChangelogModalComponent } from 'angular/app/shared/components/changelog-modal/changelog-modal.component';
import {
  selectAuthVendor,
  selectInvoicingOpenCount,
} from '../../core/store/reducers';
import { PaymentProvider } from '../../core/models/entities/vendor';
import { gsap } from 'gsap';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

@Component({
  selector: 'app-sidebar-links',
  template: `
    <div class="flex min-h-full flex-col justify-between pt-4">
      <nav class="h-full overflow-y-auto">
        <div class="space-y-1 px-2">
          <!-- Sélecteur vendor mobile -->
          <div id="sidebar-users" class="pb-2 lg:hidden">
            @if (currentUser()?.vendors?.length > 1) {
              <app-vendor-selection></app-vendor-selection>
            }
            @if (currentUser()?.vendors?.length === 1) {
              <div class="py-4 text-sm uppercase">
                {{ currentUser()?.vendor?.name }}
              </div>
            }
          </div>

          <!-- Réservations -->
          <div id="sidebar-bookings">
            <ng-template [appHasPermission]="BookingPermission.ViewAny">
              <app-sidebar-link
                [label]="'nav.bookings' | transloco"
                icon="calendar"
                routerLink="/reservations"
              >
                @if (pendingBookings$ | async) {
                  <app-badge
                    variant="primary"
                    appearance="solid"
                    [notification]="true"
                  >
                    {{
                      ((pendingBookingsCount$ | async) || 0) > 9
                        ? '9+'
                        : (pendingBookingsCount$ | async) || 0
                    }}
                  </app-badge>
                }
              </app-sidebar-link>
            </ng-template>
          </div>

          <!-- Salles -->
          <ng-template [appHasPermission]="RoomPermission.Store">
            <div id="sidebar-rooms">
              <app-sidebar-link
                [label]="'nav.rooms' | transloco"
                icon="warehouse"
                routerLink="/salles"
              ></app-sidebar-link>
            </div>
          </ng-template>

          <!-- Codes promo -->
          <ng-template [appHasPermission]="CouponPermission.ViewAny">
            <div id="sidebar-coupons">
              <app-sidebar-link
                [label]="'nav.coupons' | transloco"
                icon="percentage"
                routerLink="/codes-promo"
              ></app-sidebar-link>
            </div>
          </ng-template>

          <!-- Section Comptabilité -->
          <ng-template [appHasPermission]="InvoicePermission.ViewAny">
            <app-sidebar-section-title
              [title]="'nav.section.accounting' | transloco"
            ></app-sidebar-section-title>

            <div id="sidebar-invoicing">
              <app-sidebar-link
                [label]="'nav.invoicing' | transloco"
                icon="calculator"
                routerLink="/facturation"
              >
                @if (openInvoicesCount()) {
                  <app-badge
                    variant="primary"
                    appearance="solid"
                    [notification]="true"
                  >
                    {{ openInvoicesCount() > 9 ? '9+' : openInvoicesCount() }}
                  </app-badge>
                }
              </app-sidebar-link>
            </div>

            @if (vendor()?.accepts_online_payments) {
              <div id="sidebar-payment-provider">
                <app-sidebar-link
                  [label]="vendor().payment_provider | titleCase"
                  icon="arrow-up-right-from-square"
                  [href]="
                    vendor()?.payment_provider === PaymentProvider.Wallee
                      ? 'https://app-wallee.com/s/' +
                        vendor()?.wallee_space_id +
                        '/space'
                      : 'https://' +
                        (vendor().test_mode || vendor().is_ercos
                          ? 'test'
                          : vendor().payrexx_instance_name) +
                        '.pay.location-salles.ch/cadmin/index.php'
                  "
                  target="_blank"
                ></app-sidebar-link>
              </div>
            }
          </ng-template>

          <!-- Section Paramètres -->
          <ng-template [appHasPermission]="UserPermission.ViewAny">
            <app-sidebar-section-title
              [title]="'nav.section.params' | transloco"
            ></app-sidebar-section-title>

            <!-- Utilisateurs -->
            <div id="sidebar-users">
              <app-sidebar-link
                [label]="'nav.users' | transloco"
                icon="user-group"
                routerLink="/utilisateurs"
              ></app-sidebar-link>
            </div>

            <!-- Réglages (collapsible) -->
            <ng-template [appHasPermission]="VendorPermission.Update">
              <div id="sidebar-settings">
                <a
                  class="flex cursor-pointer items-center gap-x-3.5 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-800 no-underline hover:bg-gray-100"
                  (click)="toggleSettings()"
                >
                  <i class="fal fa-cog fa-fw text-base"></i>
                  <span class="translate-y-px">{{ 'nav.settings' | transloco }}</span>
                  <i
                    class="fal fa-chevron-down size-4 transition-transform"
                    [ngClass]="{
                      'rotate-180': !settingsCollapsed(),
                    }"
                  ></i>
                </a>
              </div>

              <div class="h-0 overflow-hidden" #collapsibleContent>
                <div
                  class="my-1 ml-5 space-y-1 border-l-2 border-gray-200 pl-4"
                >
                  <app-sidebar-link
                    [label]="'nav.locales' | transloco"
                    routerLink="/reglages/langues"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    [label]="'nav.rates' | transloco"
                    routerLink="/reglages/groupes-de-tarifs"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    [label]="'nav.cancel-conditions' | transloco"
                    routerLink="/reglages/conditions-annulation"
                    [indent]="true"
                  ></app-sidebar-link>

                  <ng-template
                    [appHasPermission]="CustomFieldPermission.ViewAny"
                  >
                    <ng-template appFeature="custom_fields" let-isBeta="isBeta">
                      <app-sidebar-link
                        [label]="'nav.custom-fields' | transloco"
                        routerLink="/reglages/champs-personnalises"
                        [indent]="true"
                      >
                        @if (isBeta) {
                          <span end class="ms-auto -translate-y-1 px-1">
                            <app-badge variant="pink">beta</app-badge>
                          </span>
                        }
                      </app-sidebar-link>
                    </ng-template>
                  </ng-template>

                  <app-sidebar-link
                    [label]="'nav.calendar-opening' | transloco"
                    routerLink="/reglages/ouverture-calendrier"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    [label]="'nav.communication-templates' | transloco"
                    routerLink="/reglages/modeles-communication"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    [label]="'nav.payment-methods' | transloco"
                    routerLink="/reglages/modes-de-paiement"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    [label]="'nav.security' | transloco"
                    routerLink="/reglages/securite"
                    [indent]="true"
                  ></app-sidebar-link>

                  <app-sidebar-link
                    [label]="'nav.test-mode' | transloco"
                    routerLink="/reglages/mode-test"
                    [indent]="true"
                  ></app-sidebar-link>
                </div>
              </div>

              <!-- Organisation -->
              <div id="sidebar-account">
                <app-sidebar-link
                  [label]="'nav.organization' | transloco"
                  icon="building"
                  routerLink="/administration"
                ></app-sidebar-link>
              </div>
            </ng-template>
          </ng-template>

          <!-- Section Divers -->
          <app-sidebar-section-title [title]="'nav.section.misc' | transloco"></app-sidebar-section-title>

          <!-- Page publique -->
          @if (currentUser()) {
            <div id="sidebar-public-page">
              <app-sidebar-link
                [label]="'nav.public-page' | transloco"
                icon="arrow-up-right-from-square"
                [href]="'/louer-salle/' + currentUser().vendor.slug"
                target="_blank"
              ></app-sidebar-link>
            </div>
          }
        </div>
      </nav>

      <!-- Footer de la sidebar -->
      <div class="mt-4 border-t border-gray-200 p-4">
        <!-- Sélecteur de langue -->
        <div class="mb-3">
          <app-locale-switcher (localeSelected)="setLocale($event)" />
        </div>

        @if (currentUser()) {
          <!-- User actions -->
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <button
                class="flex size-8 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                [routerLink]="['/compte']"
                routerLinkActive="bg-gray-100"
                data-cy="user-profile"
                [title]="'nav.my-account' | transloco"
              >
                <i class="fal fa-user-pen"></i>
              </button>
              <button
                class="flex size-8 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                (click)="logout()"
                [title]="'nav.logout' | transloco"
              >
                <i class="fal fa-power-off"></i>
              </button>
            </div>

            <button
              class="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
              (click)="openChangelogModal()"
            >
              <span>{{ 'nav.changelog-button' | transloco }}</span>
              @if (changelogService.hasUnreadChanges) {
                <span class="h-2 w-2 shrink-0 self-start rounded-full bg-pink-500"></span>
              }
            </button>
          </div>
        }
      </div>
    </div>
  `,
  styles: [],
  standalone: false,
})
export class SidebarLinksComponent extends LogoutBaseComponent {
  RoomPermission = RoomPermission;
  BookingPermission = BookingPermission;
  UserPermission = UserPermission;
  VendorPermission = VendorPermission;
  InvoicePermission = InvoicePermission;
  CouponPermission = CouponPermission;
  currentUser = this.store.selectSignal(fromStore.selectAuthUser);
  pendingBookings$ = this.store
    .select(fromStore.selectAllPendingBookingsCount)
    .pipe(map((pendingBookings) => pendingBookings > 0));
  pendingBookingsCount$ = this.store.select(
    fromStore.selectAllPendingBookingsCount,
  );
  openInvoicesCount = this.store.selectSignal(selectInvoicingOpenCount);
  vendor = this.store.selectSignal(selectAuthVendor);
  settingsCollapsed = signal(true);
  collapsibleContent = viewChild<ElementRef>('collapsibleContent');
  protected readonly PaymentProvider = PaymentProvider;
  router = inject(Router);
  route = inject(ActivatedRoute);

  constructor(
    private store: Store,
    public changelogService: ChangelogService,
    modalService: ModalService,
    authService: AuthService,
    toastService: ToastService,
  ) {
    super(modalService, authService, toastService);
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.toggleSettingsWhenNecessary();
      });
  }

  ngAfterViewInit() {
    this.toggleSettingsWhenNecessary();
  }

  openChangelogModal() {
    this.modalService.open(ChangelogModalComponent, {
      title: this.translocoService.translate('nav.changelog'),
      showActionButtons: false,
      size: 'xl',
    });
  }

  /**
   * Ferme le collapse des réglages si on quitte les réglages
   * Ouvre le collapse des réglages si on est arrivé sur la page par un autre lien (on n'a pas ouvert manuellement le menu)
   */
  toggleSettingsWhenNecessary() {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    const isChildRouteSettings =
      !!currentRoute.parent && this.router.url.startsWith('/reglages');

    if (isChildRouteSettings && this.settingsCollapsed()) {
      this.toggleSettings(); // on ouvre les réglages si ce n'est pas le cas
    } else if (!isChildRouteSettings && !this.settingsCollapsed()) {
      this.toggleSettings(); // on a quitté les réglages donc on ferme le collapse
    }
  }

  toggleSettings() {
    this.settingsCollapsed.set(!this.settingsCollapsed());
    const content = this.collapsibleContent().nativeElement;

    if (this.settingsCollapsed()) {
      gsap.to(content, { height: 0, duration: 0.3, ease: 'power2.inOut' });
    } else {
      gsap.to(content, { height: 'auto', duration: 0.3, ease: 'power2.inOut' });
    }
  }

  protected readonly CustomFieldPermission = CustomFieldPermission;

  localeService = inject(LocaleService);

  setLocale(locale: AppLocale) {
    this.localeService.setLocale(locale);
  }
}
